import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAllPrinters, getGroupPrinters, updateGroupPrinters } from "../../serviceBroker/printerApiServiceBroker";
import { GroupPrinterResult, PrintersModelResult } from "../../models/printers/printersModel";
import { ButtonBox, LoadingBox, SelectBox, TextBox, ToastBox } from "../index";
import { getLabelName, getLookUpItemValue, isArabicCurrentLanguage } from "../../utils";
import { FieldArray, Form, Formik } from "formik";
import React from "react";
import {LookupItemModel, RowStateEnum, ToastModel} from "../../models";

type Props = {
    setShowPrinterGroup:React.Dispatch<React.SetStateAction<boolean>>;
};

const PrinterForGroupComponent = ({setShowPrinterGroup}:Props) => {
    //#region state
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState<{ groupOfPrinters: GroupPrinterResult[] }>({
            groupOfPrinters: [],
    });
    const [allPrinters, setAllPrinters] = useState<PrintersModelResult[]>([]);
    const [toastModel, setToastModel] = useState<ToastModel>({ show: false });
    const printerOptions = useMemo(() => {
        return allPrinters.map((printer) => ({
            value: printer.Name,
            name: printer.Name,
            nameAr: printer.Name,
        }));
    }, [allPrinters]);
    //#endregion
    //#region functions
    const handleUpdateGroupOfPrinters = async (values: GroupPrinterResult[]) => {
        try {
            setLoading(true);
            await updateGroupPrinters({ request: values });
            setToastModel({
                ...toastModel,
                show: true,
                variant: "success",
            });
            setLoading(false);
        } catch (error:any) {
            setLoading(false);
            setToastModel({
                ...toastModel,
                show: true,
                variant: "danger",
                body:getLabelName(error)
            });
        }
    };


    //#endregion
    //#region useEffect
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                const [printers, groupofPrinters] = await Promise.all([
                    getAllPrinters(),
                    getGroupPrinters(),
                ]);

                setAllPrinters(printers.Result);
                setInitialValues({ groupOfPrinters: groupofPrinters.Result });
            } catch (error) {
                console.error("Error fetching initial data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData().then(() => {});
    }, []);

    useEffect(()=>{
        if(toastModel.show){
            setShowPrinterGroup(false)
        }
    },[toastModel.show])
    //#endregion

    if(loading){
        return <LoadingBox />
    }

    return (
        <>
            {toastModel.show && (
                <ToastBox
                    isShown={toastModel.show}
                    header={toastModel.header}
                    body={toastModel.body}
                    variant={toastModel.variant}
                    delayDuration={toastModel.delayDuration}
                    onCloseEvent={() => {
                    setToastModel({ ...toastModel, show: false });
                    }}
                />
            )}
            {loading ? (
                <LoadingBox />
            ) : initialValues.groupOfPrinters.length > 0 ? (
                <>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize
                        onSubmit={async (values) => {
                            let updatedValues = values.groupOfPrinters.filter(
                                (group) => group.rowState === RowStateEnum.Update
                            );
                            await handleUpdateGroupOfPrinters(updatedValues);
                        }}
                    >
                        {({ values, setFieldValue }) => (
                            <Form id="set-printer-for-group">
                                <FieldArray name="groupOfPrinters">
                                    {({ push }) => (
                                        <div style={{ maxHeight: "400px", overflowY: "scroll" }}>
                                            {values.groupOfPrinters.map((group, index) => (
                                                <PrinterGroup
                                                    key={group.CategoryID}
                                                    group={group}
                                                    index={index}
                                                    setFieldValue={setFieldValue}
                                                    printerOptions={printerOptions}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </FieldArray>
                            </Form>
                        )}
                    </Formik>
                    <div className="mt-2 d-flex gap-3">
                        <ButtonBox variant="success" type="submit" form="set-printer-for-group">
                            {getLabelName("save")}
                        </ButtonBox>
                        <ButtonBox variant="danger" type="button" onClick={() => setShowPrinterGroup(false)}>
                            {getLabelName("cancel")}
                        </ButtonBox>
                    </div>
                </>
            ) : (
                <div className="text-center">{getLabelName("no data")}</div>
            )}
        </>
    );
}
export default PrinterForGroupComponent
//#region  Reusable  component
const PrinterGroup = React.memo(
    ({
         group,
         index,
         setFieldValue,
         printerOptions,
     }: {
        group: GroupPrinterResult;
        index: number;
        setFieldValue: (
            field: string,
            value: any,
            shouldValidate?: boolean | undefined
        ) => void;
        printerOptions: { value: string; name: string; nameAr: string }[];
    }) => {
        //#region state
        const isArabic = isArabicCurrentLanguage();
        const printersSelectBoxMultiselectRef = useRef<any>();
//#endregion
        //#region functions
        const handlePrinterChange = useCallback((index: number, e: LookupItemModel) => {
            const val = getLookUpItemValue(e);
            setFieldValue(`groupOfPrinters.${index}.PrinterName`, val);
            setFieldValue(`groupOfPrinters.${index}.rowState`, 2);
        }, [setFieldValue]);
//#endregion
        //#region html
        return (
            <div key={group.CategoryID} className="container">
                <div className="row">
                    <div className="col-sm-3 col-md-3">
                        {
                            isArabic ?
                                (group.CategoryNameAr)
                                :
                                (group.CategoryNameEn)
                        }
                    </div>
                    <div className="col-sm-6 col-md-6">
                        <SelectBox
                            labelName=""
                            inputName={`groupOfPrinters.${index}.PrinterName`}
                            isSingleSelect={true}
                            selectedValues={[group.PrinterName]}
                            source={printerOptions}
                            onStatusChange={(e: any) => handlePrinterChange(index, e)}
                            multiselectRef={printersSelectBoxMultiselectRef}
                            usePortalTarget={false}
                        />
                    </div>
                    <div className="col-sm-3 col-md-3">
                        <TextBox
                            labelName="Num. Copy"
                            type="number"
                            placeHolder={getLabelName("Num. Copy")}
                            inputName={`groupOfPrinters.${index}.NumberOfCopy`}
                            inputValue={group.NumberOfCopy}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue(`groupOfPrinters.${index}.NumberOfCopy`, Number(e.target.value))
                                setFieldValue(`groupOfPrinters.${index}.rowState`, 2)
                            }
                            }
                        />
                    </div>
                </div>
            </div>
        );
        //#endregion
    }
);
//#endregion